/**
 * Shared Blog credits sync for dashboard + approval controls.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useLoadUser } from "../../../Utils/customHooks/useLoadUser";
import {
  applyGenerationSpend,
  applyPublishSpend,
  canSpendGenerationCredits,
  canSpendPublishCredit,
  generationCreditCost,
  hasActiveBlogPlan,
  isBlogCreditsEnforced,
  isBlogPublishingAllowed,
  syncBlogCreditsWithStripe,
  type BlogCreditsState,
  type BlogGenerationSpendKind,
} from "../../../Utils/SupabaseServices/blogCredits";
import { useTemplateSettings } from "../MasterTemplateEditor/useTemplateSettings";

export function useBlogCredits(userId?: string, username?: string) {
  const { isAdmin } = useAuth();
  const { data: loadedUser, isLoading: userLoading } = useLoadUser(username);
  const userData =
    loadedUser && loadedUser !== false ? loadedUser : undefined;
  const { tweaks, applyTweaksAndSaveNow, loading } = useTemplateSettings(userId);
  const [local, setLocal] = useState<BlogCreditsState | null>(null);
  const didMonthSync = useRef(false);

  // Don't treat "no plan" as locked until settings + user (stripe_detail) have loaded.
  const ready = !loading && !(username && userLoading);

  const synced = useMemo(
    () => syncBlogCreditsWithStripe(local ?? tweaks?.blogCredits, userData),
    [local, tweaks?.blogCredits, userData],
  );

  const persist = useCallback(
    async (next: BlogCreditsState) => {
      setLocal(next);
      const ok = await applyTweaksAndSaveNow({ blogCredits: next });
      if (!ok) setLocal(null);
      return ok;
    },
    [applyTweaksAndSaveNow],
  );

  // Persist Stripe month reset / plan detection once per mount when settings load.
  useEffect(() => {
    if (loading || !userId || didMonthSync.current) return;
    const fromTweaks = syncBlogCreditsWithStripe(tweaks?.blogCredits, userData);
    const raw = tweaks?.blogCredits as BlogCreditsState | undefined;
    const drifted =
      fromTweaks.plan !== (raw?.plan ?? null) ||
      fromTweaks.generationRemaining !== (raw?.generationRemaining ?? 0) ||
      fromTweaks.publishRemaining !== (raw?.publishRemaining ?? 0) ||
      fromTweaks.periodStart !== (raw?.periodStart ?? null);
    if (drifted && fromTweaks.plan) {
      didMonthSync.current = true;
      void persist(fromTweaks);
    } else if (fromTweaks.plan || raw?.plan) {
      didMonthSync.current = true;
    }
  }, [loading, userId, tweaks?.blogCredits, userData, persist]);

  const enforced = isBlogCreditsEnforced(synced, userData);
  const publishingAllowed = isBlogPublishingAllowed({
    isAdmin: Boolean(isAdmin),
    credits: synced,
    userData,
  });
  const hasPlan = hasActiveBlogPlan(synced, userData);

  const assertGeneration = useCallback(
    (kind: BlogGenerationSpendKind) => {
      const cost = generationCreditCost(kind);
      return canSpendGenerationCredits(synced, cost, {
        isAdmin: Boolean(isAdmin),
        userData,
      });
    },
    [synced, isAdmin, userData],
  );

  const spendGeneration = useCallback(
    async (kind: BlogGenerationSpendKind) => {
      const cost = generationCreditCost(kind);
      const check = canSpendGenerationCredits(synced, cost, {
        isAdmin: Boolean(isAdmin),
        userData,
      });
      if (!check.ok) return check;
      if (isAdmin || cost <= 0) return { ok: true as const };
      const next = applyGenerationSpend(synced, cost);
      const ok = await persist(next);
      return ok
        ? { ok: true as const }
        : { ok: false as const, message: "Couldn't update generation credits." };
    },
    [synced, isAdmin, userData, persist],
  );

  const spendPublish = useCallback(async () => {
    const check = canSpendPublishCredit(synced, {
      isAdmin: Boolean(isAdmin),
      userData,
    });
    if (!check.ok) return check;
    if (isAdmin) return { ok: true as const };
    const next = applyPublishSpend(synced);
    const ok = await persist(next);
    return ok
      ? { ok: true as const }
      : { ok: false as const, message: "Couldn't update publishing credits." };
  }, [synced, isAdmin, userData, persist]);

  return {
    loading,
    ready,
    userData,
    isAdmin: Boolean(isAdmin),
    credits: synced,
    enforced,
    hasPlan,
    publishingAllowed,
    persist,
    assertGeneration,
    spendGeneration,
    spendPublish,
  };
}
